/**
 * アイステイションズ路線マスタ (`istations-lines.json`) のフェッチ。
 *
 * - WebApp 内に同梱した istations-lines.json を `/data/istations-lines.json` 経由で読み込む。
 * - 既存の線区マスタ (`routes-master.json` / `data/routes.ts`) と同じ配布方式を採る
 *   (CloudFront 配下の同居アセットとして配信)。
 *
 * 【ATOS 線区マスタ (`routes.ts` / `RouteMaster`) との関係】
 *
 * 本マスタは **ATOS の線区マスタとは独立した別系統** の路線一覧である。
 * 接続線遅延コンテンツの `route_id` は ATOS の線名コード (例: `"横浜"`) だが、
 * 「運行情報の参照元」として紐付けるアイステイションズ路線の `route_id` は
 * アイステイションズ側の路線コード (例: `"3101"` = 東急東横線) であり、
 * **両者が一致しない組み合わせを運用側で自由に選べること** が要件である。
 *
 * そのため ATOS ↔ アイステイションズの相互変換は行わず、UI 上も別プルダウンとして
 * 扱う (ATOS の route_id 選択と連動させない)。紐付け自体は静的マスタではなく
 * 運用設定 (コンテンツ設定 = `properties.istations_lines`) 側に保持するため、
 * 対応関係の追加・変更にデプロイは不要となる。
 */

/** アイステイションズ路線マスタの 1 件。 */
export interface IStationsLine {
  /** アイステイションズ側の路線コード (例: `"3101"`)。ATOS の route_id とは別系統。 */
  readonly route_id: string;
  /** アイステイションズ側の路線名称 (例: `"東急東横線"`)。プルダウンの表示に使う。 */
  readonly route_name: string;
}

/** `istations-lines.json` のファイル全体の形。 */
export interface IStationsLinesFile {
  lines: IStationsLine[];
}

/** istations-lines.json を fetch して返す。 */
export async function fetchIStationsLines(): Promise<IStationsLinesFile> {
  const res = await fetch('/data/istations-lines.json', { cache: 'no-store' });
  if (!res.ok) {
    throw new Error(`Failed to fetch istations-lines.json: HTTP ${res.status}`);
  }
  return (await res.json()) as IStationsLinesFile;
}

/**
 * route_id に対応するアイステイションズ路線名称を返す。
 *
 * マスタに存在しない route_id (マスタ更新前に保存された運用設定など) の場合は
 * `undefined` を返す。呼び出し側は保存済みのスナップショット `route_name` →
 * `route_id` の順にフォールバックして表示する。
 */
export function findIStationsLineName(
  lines: readonly IStationsLine[] | undefined,
  routeId: string,
): string | undefined {
  return (lines ?? []).find((l) => l.route_id === routeId)?.route_name;
}
